import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";

import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { getEventCreatedByInfosString, getTranslation } from "../../extras";
import {
  createOrderPaymentMethod,
  deleteOrderPaymentMethod,
} from "../../api/paymentMethods";
import { addOrderBenefit, removeOrderBenefit } from "../../api/benefits";
import {
  fetchOrderDetails,
  setLoadingOrderDetail,
  setShowOrderInfosModal,
} from "../../store/actions/ordersSlice";
import CustomerService from "../../api/customer";

// APP COMPONENTS
import JsonModal from "../modals/jsonShow/jsonShow";
import OrderTicket from "../ticket";
import EventsInfos from "./modal/orderEvensInfos";
import RequestCancel from "../modals/requestCancelModal";
import CustomerInfos from "./modal/customerInfos";
import ProductsInfos from "./modal/orderProductsInfos";
import DeliveryInfos from "./modal/orderDeliveryInfos";
import FinancialInfos from "./modal/orderFinancialInfos";
import FiscalInfoModal from "./fiscal/FiscalInfoModal";
import OrderModalActions from "./modal/OrderModalActions";
import ExtraDeliveryDialog from "../dialogs/orders/ExtraDeliveryDialog";
import SummaryInfos from "./modal/orderSummaryInfos";

// CUSTOM HOOKS
import useMapViewState from "../../hooks/useMapViewState";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, keyframes, styled } from "@mui/material/styles";

// ICONS
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TimelineIcon from "@mui/icons-material/Timeline";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// COLORS
import { yellow } from "@mui/material/colors";
import { validateArrayIncludes } from "../../common/validators";
import { ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED } from "../../types/order";
import { extraDelivery } from "../../api/order";
import { printOrderTicket } from "../../utils/printService";

// ─── Styled Components ───────────────────────────────────────────────────────

const ModalShell = styled(Paper)(({ theme, mapViewState }) => {
  const isMapHidden = mapViewState === "hidden";
  const base = {
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    borderRadius: 0,
    border: `1px solid ${theme.palette.divider}`,
  };

  const desktopInset = isMapHidden
    ? { top: "1.5%", left: "1.5%", right: "34%", bottom: "1.5%" }
    : { top: "1.5%", left: "1.5%", right: "1.5%", bottom: "1.5%" };

  return {
    ...base,
    top: 0,
    left: 0,
    right: isMapHidden ? "33.333333%" : 0,
    bottom: 0,
    [theme.breakpoints.up("md")]: {
      ...desktopInset,
      borderRadius: 6,
    },
    [theme.breakpoints.down("md")]: {
      right: 0,
    },
  };
});

const TopBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.25, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  minHeight: 52,
  gap: theme.spacing(1),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const TopBarLeft = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  flexWrap: "wrap",
  minWidth: 0,
}));

const TopBarRight = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  flexWrap: "wrap",
}));

const OrderIndexLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
}));

const ActionBtn = styled(IconButton)(({ theme, variant }) => {
  const map = {
    danger: {
      bg: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.dark,
      border: alpha(theme.palette.error.main, 0.2),
    },
    primary: {
      bg: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.dark,
      border: alpha(theme.palette.primary.main, 0.2),
    },
    success: {
      bg: alpha(theme.palette.success.main, 0.08),
      color: theme.palette.success.dark,
      border: alpha(theme.palette.success.main, 0.2),
    },
    default: {
      bg: alpha(theme.palette.grey[500], 0.06),
      color: theme.palette.grey[600],
      border: theme.palette.divider,
    },
  };

  const v = map[variant] || map.default;

  return {
    borderRadius: 4,
    width: 32,
    height: 32,
    backgroundColor: v.bg,
    color: v.color,
    border: `1px solid ${v.border}`,
    "&:hover": {
      backgroundColor: alpha(v.color, 0.12),
    },
    "&:disabled": {
      backgroundColor: alpha(theme.palette.grey[200], 0.4),
      color: theme.palette.grey[400],
      borderColor: theme.palette.divider,
    },
  };
});

const SplitLayout = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

// ─── Left Panel (Ticket) ────────────────────────────────────────────────────

const TicketPane = styled(Box)(({ theme }) => ({
  width: "38%",
  minWidth: 320,
  maxWidth: 440,
  display: "flex",
  flexDirection: "column",
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.secondary,
  flexShrink: 0,
  position: "relative",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const TicketScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2.5),
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.grey[400], 0.35),
    borderRadius: 3,
    "&:hover": {
      backgroundColor: alpha(theme.palette.grey[500], 0.5),
    },
  },
}));

const TicketCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 380,
  borderRadius: 4,
  backgroundColor: yellow[50],
  boxShadow: theme.shadows[2],
  padding: theme.spacing(2.5),
  border: `1px solid ${alpha(yellow[200], 0.6)}`,
}));

const TicketFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  justifyContent: "center",
}));

const PrintBtn = styled(Button)(({ theme }) => ({
  borderRadius: 4,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  padding: theme.spacing(0.75, 3),
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
}));

// ─── Right Panel (Content) ──────────────────────────────────────────────────

const ContentPane = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minWidth: 0,
}));

const TabBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflowX: "auto",
  flexShrink: 0,
  "&::-webkit-scrollbar": { height: 0 },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const TabItem = styled(Button)(({ theme, active }) => ({
  borderRadius: 0,
  textTransform: "none",
  fontWeight: active ? 600 : 400,
  fontSize: "0.8rem",
  padding: theme.spacing(1.25, 2),
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: "transparent",
  borderBottom: active
    ? `2px solid ${theme.palette.primary.main}`
    : "2px solid transparent",
  minWidth: "auto",
  whiteSpace: "nowrap",
  gap: theme.spacing(0.75),
  transition: "color 0.15s, border-color 0.15s",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.primary.main,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.1rem",
  },
}));

const ContentScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.default,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.grey[400], 0.35),
    borderRadius: 3,
    "&:hover": {
      backgroundColor: alpha(theme.palette.grey[500], 0.5),
    },
  },
}));

// ─── Mobile Tab Pills ────────────────────────────────────────────────────────

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const MobilePillBar = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
}));

const MobilePill = styled(Button)(({ theme, active }) => ({
  borderRadius: 20,
  textTransform: "none",
  fontWeight: active ? 700 : 500,
  fontSize: "0.8rem",
  padding: theme.spacing(0.5, 1.75),
  height: 32,
  minWidth: "auto",
  gap: theme.spacing(0.5),
  transition: "all 0.18s ease",
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.1)
    : "transparent",
  border: `1.5px solid ${
    active
      ? alpha(theme.palette.primary.main, 0.35)
      : theme.palette.divider
  }`,
  "& .MuiSvgIcon-root": { fontSize: "0.95rem" },
  "&:hover": {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.grey[500], 0.06),
  },
}));

// ─── Constants ──────────────────────────────────────────────────────────────

const BASE_TABS = [
  { label: "Resumo", icon: <AutoAwesomeIcon />, value: "summary" },
  { label: "Ticket", icon: <ReceiptIcon />, value: "ticket" },
];

const PAYMENT_TAB = { label: "Pagamento", icon: <PaymentsIcon />, value: "financial" };

const RETRY_DELAY_MS = 3000;

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InfoModal({
  updateOrderStatus,
  cancelOrder,
  closeModal,
}) {
  const dispatch = useDispatch();
  const componentRef = useRef(null);
  const { mapViewState } = useMapViewState();

  const deliveryCenter = useSelector(
    (state) => state.deliveryCenter.deliveryCenter
  );
  const loadingOrderDetail = useSelector(
    (state) => state.orders.loadingOrderDetail
  );
  const orderDetail = useSelector((state) => state.orders.orderDetails);
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );
  const orderInfosModalOrderId = useSelector(
    (state) => state.orders.orderInfosModalOrderId
  );

  const [activeTab, setActiveTab] = useState("summary");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEmitFiscalModal, setShowEmitFiscalModal] = useState(false);
  const [showEventBodyModal, setShowEventBodyModal] = useState(false);
  const [extraDeliveryDialogOpen, setExtraDeliveryDialogOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState(false);
  const [eventCreatedBy, setEventCreatedBy] = useState(false);
  const [eventCode, setEventCode] = useState(false);
  const [eventBody, setEventBody] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [deliveryReason, setDeliveryReason] = useState("");
  const [driverError, setDriverError] = useState("");
  const [driverBlockedWarning, setDriverBlockedWarning] = useState("");
  const [isSubmittingExtraDelivery, setIsSubmittingExtraDelivery] =
    useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [benefitsState, setBenefitsState] = useState([]);
  const [isTogglingBlacklist, setIsTogglingBlacklist] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(false);

  const hasFiscalConfig = useMemo(() => {
    return Boolean(deliveryCenter?.fk_fiscal_info);
  }, [deliveryCenter?.fk_fiscal_info]);

  const showPaymentTab = useMemo(() => {
    if (!orderDetail) return false;
    const isEditable = orderDetail.channel === "SLAPIE_POS" && orderDetail.status_number < 34;
    return isEditable && parseFloat(orderDetail.payment_pending) > 0;
  }, [orderDetail]);

  const visibleTabs = useMemo(() => {
    if (!showPaymentTab) return BASE_TABS;
    return [BASE_TABS[0], PAYMENT_TAB, BASE_TABS[1]];
  }, [showPaymentTab]);

  const canCreateExtraDelivery = useMemo(() => {
    if (!orderDetail) {
      return false;
    }

    const isDeliveryOrder = orderDetail.order_type === "DELIVERY";
    const statusAllowed = !validateArrayIncludes(
      ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED,
      orderDetail.status_number
    );

    return isDeliveryOrder && statusAllowed;
  }, [orderDetail?.order_type, orderDetail?.status_number]);

  const hasCustomer = useMemo(() => {
    if (!orderDetail?.customer) {
      return false;
    }

    const hasCustomerId = Boolean(orderDetail.customer.customer_id);

    return hasCustomerId;
  }, [orderDetail?.customer]);

  const isCustomerBlacklisted = useMemo(() => {
    if (!hasCustomer) {
      return false;
    }

    return Boolean(orderDetail.customer.is_blacklisted);
  }, [hasCustomer, orderDetail?.customer?.is_blacklisted]);

  const handleReactToPrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Pedido-${orderDetail?.order_index}`,
    pageStyle: `
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
      }
    `,
  });

  const loadData = useCallback(async () => {
    if (!orderInfosModalOrderId) {
      return;
    }

    try {
      await dispatch(fetchOrderDetails({ orderId: orderInfosModalOrderId }));
    } catch (err) {
      console.error("Error loading order details:", err);
    } finally {
      dispatch(setLoadingOrderDetail(false));
    }
  }, [dispatch, orderInfosModalOrderId]);

  const loadDataWithTimeout = useCallback(async () => {
    try {
      dispatch(setLoadingOrderDetail(true));

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      await loadData();
    } catch (e) {
      console.error("Error loading data with timeout:", e);
    }
  }, [dispatch, loadData]);

  useEffect(() => {
    if (!orderDetail) {
      return;
    }

    setPaymentMethods(orderDetail.payment || []);
    setBenefitsState(orderDetail.benefits || []);
  }, [orderDetail]);

  useEffect(() => {
    if (!orderInfosModalOrderId) {
      return;
    }

    loadData();
  }, [orderInfosModalOrderId, loadData]);

  // When WebSocket print fails, switch to ticket tab and auto-trigger browser print
  useEffect(() => {
    if (!pendingPrint || activeTab !== "ticket") return;

    const timer = setTimeout(() => {
      if (componentRef.current) {
        try {
          handleReactToPrint();
          dispatchSnackBarMessage("print-browser", "Imprimindo via navegador...", "info");
        } catch (err) {
          console.error("Browser print error:", err);
          dispatchSnackBarMessage("print-fail", "Erro ao imprimir. Verifique a impressora.", "error");
        }
      } else {
        dispatchSnackBarMessage("print-noref", "Ticket ainda não carregado, tente novamente.", "warning");
      }
      setPendingPrint(false);
    }, 350);

    return () => clearTimeout(timer);
  }, [pendingPrint, activeTab, handleReactToPrint]);

  const handleUpdateOrderStatus = useCallback(
    (action) => {
      if (!orderDetail) {
        return;
      }

      updateOrderStatus(orderDetail, action);
    },
    [orderDetail, updateOrderStatus]
  );

  const handleCancelOrderModal = useCallback(() => {
    setShowCancelModal((prev) => !prev);
  }, []);

  const cancelOrderHandler = useCallback(
    (code, reason) => {
      if (!orderDetail) {
        return;
      }

      cancelOrder(orderDetail, code, reason);
      handleCancelOrderModal();
      dispatch(setShowOrderInfosModal({ show: false }));
    },
    [orderDetail, cancelOrder, handleCancelOrderModal, dispatch]
  );

  const handleEventBodyModal = useCallback(
    (body = null, code = null, createdBy = null) => {
      const title = getTranslation(code);

      if (body) {
        setEventCode(code);
        setEventBody(body);
        setEventCreatedBy(getEventCreatedByInfosString(createdBy));
      }

      setEventTitle(title ? title.toUpperCase() : "");
      setShowEventBodyModal((prev) => !prev);
    },
    []
  );

  const handleEmitFiscalModal = useCallback(() => {
    setShowEmitFiscalModal((prev) => !prev);
  }, []);

  const handleExtraDeliveryDialogOpen = useCallback(() => {
    setExtraDeliveryDialogOpen(true);
    setSelectedDriver(null);
    setDeliveryReason("");
    setDriverError("");
    setDriverBlockedWarning("");
  }, []);

  const handleExtraDeliveryDialogClose = useCallback(() => {
    setExtraDeliveryDialogOpen(false);
    setSelectedDriver(null);
    setDeliveryReason("");
    setDriverError("");
    setDriverBlockedWarning("");
  }, []);

  const handleDriverSelectChange = useCallback((event, value) => {
    setSelectedDriver(value?.value || null);
    setDriverError("");

    if (value?.isBlocked) {
      setDriverBlockedWarning(
        "Este motorista já possui uma corrida ativa. A entrega será adicionada à corrida atual."
      );
    } else {
      setDriverBlockedWarning("");
    }
  }, []);

  const handleReasonChange = useCallback((value) => {
    setDeliveryReason(value);
  }, []);

  const validateExtraDeliveryForm = useCallback(() => {
    if (!selectedDriver) {
      setDriverError("Por favor, selecione um motorista");
      return false;
    }

    return true;
  }, [selectedDriver]);

  const handleExtraDeliveryConfirm = useCallback(async () => {
    if (!validateExtraDeliveryForm() || !orderDetail) {
      return;
    }

    try {
      setIsSubmittingExtraDelivery(true);

      await extraDelivery({
        orderId: orderDetail.order_id,
        driver_id: selectedDriver,
        reason: deliveryReason.trim() || null,
      });

      dispatchSnackBarMessage("extra-ok", "Corrida extra criada com sucesso!", "success");

      handleExtraDeliveryDialogClose();
      await loadData();
    } catch (error) {
      console.error("Error creating extra delivery:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro ao criar corrida extra. Tente novamente.";

      dispatchSnackBarMessage("extra-err", errorMessage, "error");
    } finally {
      setIsSubmittingExtraDelivery(false);
    }
  }, [
    validateExtraDeliveryForm,
    orderDetail,
    selectedDriver,
    deliveryReason,
    handleExtraDeliveryDialogClose,
    loadData,
  ]);

  const getBlacklistTooltipText = useCallback(() => {
    if (!hasCustomer) {
      return "Sem cliente vinculado";
    }

    if (isCustomerBlacklisted) {
      return "Remover da Lista de Não Confiáveis";
    }

    return "Adicionar à Lista de Não Confiáveis";
  }, [hasCustomer, isCustomerBlacklisted]);

  const getBlacklistSuccessMessage = useCallback((newBlacklistStatus) => {
    if (newBlacklistStatus) {
      return "Cliente adicionado à Lista de Não Confiáveis";
    }

    return "Cliente removido da Lista de Não Confiáveis";
  }, []);

  const handleToggleBlacklist = useCallback(async () => {
    if (!hasCustomer || !orderDetail?.customer?.customer_id) {
      return;
    }

    try {
      setIsTogglingBlacklist(true);

      const newBlacklistStatus = !isCustomerBlacklisted;
      const customerId = orderDetail.customer.customer_id;

      await CustomerService.setCustomerBlacklist(
        customerId,
        newBlacklistStatus
      );

      const successMessage = getBlacklistSuccessMessage(newBlacklistStatus);

      dispatchSnackBarMessage("blacklist-ok", successMessage, "success");

      await loadData();
    } catch (error) {
      console.error("Error toggling customer blacklist:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro ao atualizar status do cliente. Tente novamente.";

      dispatchSnackBarMessage("blacklist-err", errorMessage, "error");
    } finally {
      setIsTogglingBlacklist(false);
    }
  }, [
    hasCustomer,
    orderDetail?.customer?.customer_id,
    isCustomerBlacklisted,
    getBlacklistSuccessMessage,
    loadData,
  ]);

  const returnTreatedPaymentMethod = useCallback(
    (paymentMethod) => {
      if (!orderDetail) {
        return null;
      }

      const { id, amount, changeFor, value } = paymentMethod;

      return {
        id: id,
        payment_type: "OFFLINE",
        payment_method: value,
        value: amount,
        card_brand: null,
        wallet_name: null,
        currency: "BRL",
        change_for: changeFor,
        fk_order: orderDetail.order_id,
        prepaid: false,
      };
    },
    [orderDetail]
  );

  const handleSetPaymentMethods = useCallback(
    async (paymentMethodsInput) => {
      try {
        const paymentMethodsArray = Array.isArray(paymentMethodsInput)
          ? paymentMethodsInput
          : paymentMethodsInput
          ? [paymentMethodsInput]
          : [];

        if (paymentMethodsArray.length === 0) {
          setPaymentMethods([]);
          return;
        }

        const methodsToDelete = paymentMethods.filter(
          (method) =>
            !paymentMethodsArray.some((newMethod) => newMethod.id === method.id)
        );

        const methodsToAdd = paymentMethodsArray.filter(
          (method) =>
            !paymentMethods.some(
              (existingMethod) => existingMethod.id === method.id
            )
        );

        if (methodsToDelete.length > 0) {
          for (const method of methodsToDelete) {
            const treatedMethod = returnTreatedPaymentMethod(method);
            if (treatedMethod) {
              await deleteOrderPaymentMethod(treatedMethod);
            }
          }

          setPaymentMethods((prev) =>
            prev.filter(
              (method) =>
                !methodsToDelete.some(
                  (deleteMethod) => deleteMethod.id === method.id
                )
            )
          );
        }

        if (methodsToAdd.length > 0) {
          for (const method of methodsToAdd) {
            const treatedMethod = returnTreatedPaymentMethod(method);
            if (treatedMethod) {
              await createOrderPaymentMethod(treatedMethod);
            }
          }

          setPaymentMethods((prev) => [...prev, ...methodsToAdd]);
        }

        if (methodsToAdd.length > 0 || methodsToDelete.length > 0) {
          await loadData();
        }
      } catch (error) {
        console.error("Error managing payment methods:", error);
        dispatchSnackBarMessage("payment-err", "Erro ao administrar os métodos de pagamento.", "error");
      }
    },
    [paymentMethods, returnTreatedPaymentMethod, loadData]
  );

  const handleAddBenefits = useCallback(
    async (benefitsData) => {
      if (!benefitsData || !orderDetail) {
        return;
      }

      try {
        await addOrderBenefit(orderDetail.order_id, benefitsData);
        await loadData();
      } catch (error) {
        console.error("Error adding benefit:", error);
        dispatchSnackBarMessage("benefit-add-err", "Erro ao adicionar desconto.", "error");
      }
    },
    [orderDetail, loadData]
  );

  const handleRemoveBenefits = useCallback(
    async (benefitToRemove) => {
      if (!benefitToRemove || !orderDetail) {
        return;
      }

      try {
        const params = { bid: benefitToRemove };
        await removeOrderBenefit(orderDetail.order_id, params);
        await loadData();
      } catch (error) {
        console.error("Error removing benefit:", error);
        dispatchSnackBarMessage("benefit-rm-err", "Erro ao remover desconto.", "error");
      }
    },
    [orderDetail, loadData]
  );

  const handlePrint = useCallback(async () => {
    if (!orderDetail) return;

    try {
      await printOrderTicket({
        order: orderDetail,
        products: orderDetail.allProducts,
        benefits: orderDetail.benefits,
      });
      dispatchSnackBarMessage("print-ok", "Ticket enviado para impressão!", "success");
    } catch (error) {
      console.error("WebSocket Print Error:", error);
      // Switch to ticket tab and let the pendingPrint effect trigger browser print
      setActiveTab("ticket");
      setPendingPrint(true);
    }
  }, [orderDetail]);

  const handleTabChange = useCallback((tabValue) => {
    setActiveTab(tabValue);
  }, []);

  const renderBlacklistButton = useCallback(() => {
    if (!hasCustomer) {
      return (
        <Tooltip
          title={getBlacklistTooltipText()}
          PopperProps={{ sx: { zIndex: 5200 } }}
        >
          <span>
            <ActionBtn variant="danger" disabled size="small">
              <BlockIcon fontSize="small" />
            </ActionBtn>
          </span>
        </Tooltip>
      );
    }

    const buttonVariant = isCustomerBlacklisted ? "success" : "danger";
    const ButtonIcon = isCustomerBlacklisted ? CheckCircleIcon : BlockIcon;

    return (
      <Tooltip
        title={getBlacklistTooltipText()}
        PopperProps={{ sx: { zIndex: 5200 } }}
      >
        <ActionBtn
          variant={buttonVariant}
          onClick={handleToggleBlacklist}
          disabled={isTogglingBlacklist}
          size="small"
        >
          {isTogglingBlacklist ? (
            <CircularProgress size={16} />
          ) : (
            <ButtonIcon fontSize="small" />
          )}
        </ActionBtn>
      </Tooltip>
    );
  }, [
    hasCustomer,
    isCustomerBlacklisted,
    isTogglingBlacklist,
    getBlacklistTooltipText,
    handleToggleBlacklist,
  ]);

  const renderTabContent = useCallback(() => {
    if (!orderDetail) {
      return null;
    }

    switch (activeTab) {
      case "summary":
        return (
          <SummaryInfos
            orderId={orderDetail.order_id}
            order={orderDetail}
            loadOrder={loadDataWithTimeout}
            onPrint={handlePrint}
            onCancel={handleCancelOrderModal}
            onFiscal={hasFiscalConfig ? handleEmitFiscalModal : null}
            onExtraDelivery={canCreateExtraDelivery ? handleExtraDeliveryDialogOpen : null}
            onToggleBlacklist={hasCustomer ? handleToggleBlacklist : null}
            isBlacklisted={isCustomerBlacklisted}
            onPayment={() => handleTabChange("financial")}
          />
        );
      case "financial":
        return (
          <FinancialInfos
            orderId={orderDetail.order_id}
            orderStatus={orderDetail.status_number}
            orderChannel={orderDetail.channel}
            paymentMethods={paymentMethods}
            paymentPending={orderDetail.payment_pending}
            setPaymentMethods={handleSetPaymentMethods}
            addBenefits={handleAddBenefits}
            removeBenefits={handleRemoveBenefits}
            changeFor={orderDetail.changeFor}
            subTotal={orderDetail.sub_total}
            total={orderDetail.total}
            tax={orderDetail.tax}
            benefits={benefitsState}
            loadData={loadData}
          />
        );
      case "ticket":
        return null;
      default:
        return null;
    }
  }, [
    orderDetail,
    activeTab,
    handlePrint,
    handleCancelOrderModal,
    handleEmitFiscalModal,
    handleExtraDeliveryDialogOpen,
    handleToggleBlacklist,
    handleTabChange,
    handleSetPaymentMethods,
    handleAddBenefits,
    handleRemoveBenefits,
    hasFiscalConfig,
    canCreateExtraDelivery,
    hasCustomer,
    isCustomerBlacklisted,
    paymentMethods,
    benefitsState,
    loadData,
  ]);

  // ─── Loading State ──────────────────────────────────────────────────────

  if (loadingOrderDetail) {
    return (
      <Modal
        aria-labelledby="order-info-modal"
        aria-describedby="order-info-modal"
        open={showOrderInfosModal}
        onClose={closeModal}
        closeAfterTransition
        sx={{ zIndex: 5000 }}
        BackdropProps={{
          sx: { backgroundColor: alpha("#000", 0.5) },
        }}
      >
        <Fade in={showOrderInfosModal} mountOnEnter unmountOnExit>
          <ModalShell mapViewState={mapViewState}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          </ModalShell>
        </Fade>
      </Modal>
    );
  }

  if (!orderDetail) {
    return null;
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <Modal
        aria-labelledby="order-info-modal"
        aria-describedby="order-info-modal"
        open={showOrderInfosModal}
        onClose={closeModal}
        closeAfterTransition
        sx={{
          zIndex: 5000,
          "&:focus-visible": { border: "none" },
        }}
        BackdropProps={{
          sx: { backgroundColor: alpha("#000", 0.5) },
        }}
      >
        <Fade in={showOrderInfosModal} mountOnEnter unmountOnExit>
          <ModalShell mapViewState={mapViewState}>
            {/* ── Top Bar ────────────────────────────────────────────── */}
            <TopBar>
              <TopBarLeft>
                <OrderIndexLabel>
                  Pedido #{orderDetail.order_index}
                </OrderIndexLabel>

                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <OrderModalActions
                    order={orderDetail}
                    loadOrder={loadDataWithTimeout}
                  />
                </Box>
              </TopBarLeft>

              <TopBarRight>
                {renderBlacklistButton()}

                <Tooltip
                  title="Cancelar Pedido"
                  PopperProps={{ sx: { zIndex: 5200 } }}
                >
                  <ActionBtn
                    variant="danger"
                    onClick={handleCancelOrderModal}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </ActionBtn>
                </Tooltip>

                <Tooltip
                  title={
                    hasFiscalConfig
                      ? "Nota Fiscal"
                      : "Nota Fiscal (Sem Configuração)"
                  }
                  PopperProps={{ sx: { zIndex: 5200 } }}
                >
                  <span>
                    <ActionBtn
                      variant="primary"
                      onClick={handleEmitFiscalModal}
                      disabled={!hasFiscalConfig}
                      size="small"
                    >
                      <ReceiptIcon fontSize="small" />
                    </ActionBtn>
                  </span>
                </Tooltip>

                {canCreateExtraDelivery && (
                  <Tooltip
                    title="Corrida Extra"
                    PopperProps={{ sx: { zIndex: 5200 } }}
                  >
                    <ActionBtn
                      variant="primary"
                      onClick={handleExtraDeliveryDialogOpen}
                      size="small"
                    >
                      <DriveEtaIcon fontSize="small" />
                    </ActionBtn>
                  </Tooltip>
                )}

                <Box
                  sx={(theme) => ({
                    width: "1px",
                    height: 20,
                    backgroundColor: theme.palette.divider,
                    mx: 0.25,
                  })}
                />

                <Tooltip
                  title="Fechar"
                  PopperProps={{ sx: { zIndex: 5200 } }}
                >
                  <ActionBtn onClick={closeModal} size="small">
                    <CloseIcon fontSize="small" />
                  </ActionBtn>
                </Tooltip>
              </TopBarRight>
            </TopBar>

            {/* ── Content ──────────────────────────────────────────── */}
            <ContentPane>
              {/* Desktop tab bar */}
              <TabBar>
                {visibleTabs.map((tab) => (
                  <TabItem
                    key={tab.value}
                    active={activeTab === tab.value ? 1 : 0}
                    onClick={() => handleTabChange(tab.value)}
                    startIcon={tab.icon}
                    disableRipple
                  >
                    {tab.label}
                  </TabItem>
                ))}
              </TabBar>

              {/* Mobile pill tab bar */}
              <MobilePillBar>
                {visibleTabs.map((tab) => (
                  <MobilePill
                    key={tab.value}
                    active={activeTab === tab.value ? 1 : 0}
                    onClick={() => handleTabChange(tab.value)}
                    startIcon={tab.icon}
                    disableRipple
                  >
                    {tab.label}
                  </MobilePill>
                ))}
              </MobilePillBar>

              {activeTab === "ticket" ? (
                <>
                  <TicketScroll
                    sx={{
                      flex: 1,
                      animation: {
                        xs: `${slideInFromRight} 0.25s ease-out both`,
                        md: "none",
                      },
                    }}
                  >
                    <TicketCard ref={componentRef}>
                      <OrderTicket
                        order={orderDetail}
                        products={orderDetail.allProducts}
                        benefits={orderDetail.benefits}
                        changeFor={orderDetail.changeFor}
                      />
                    </TicketCard>
                  </TicketScroll>
                  <TicketFooter>
                    <PrintBtn
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                    >
                      Imprimir Ticket
                    </PrintBtn>
                  </TicketFooter>
                </>
              ) : (
                <ContentScroll
                  sx={{
                    animation: {
                      xs: `${slideInFromLeft} 0.25s ease-out both`,
                      md: "none",
                    },
                    ...(activeTab === "summary" && {
                      overflow: "hidden",
                      padding: 0,
                      display: "flex",
                      flexDirection: "column",
                    }),
                  }}
                >
                  {renderTabContent()}
                </ContentScroll>
              )}
            </ContentPane>
          </ModalShell>
        </Fade>
      </Modal>

      {/* ── Sub-modals ─────────────────────────────────────────────────── */}

      <Box sx={{ zIndex: 5200 }}>
        {showCancelModal && (
          <RequestCancel
            show={showCancelModal}
            order={orderDetail}
            closeModal={handleCancelOrderModal}
            submitCancel={cancelOrderHandler}
          />
        )}
      </Box>

      <Box sx={{ zIndex: 5200 }}>
        {showEmitFiscalModal && (
          <FiscalInfoModal
            orderId={orderDetail.order_id}
            orderValue={orderDetail.total}
            orderIndex={orderDetail.order_index}
            orderStatusNumber={orderDetail.status_number}
            customerIdentification={orderDetail.customer?.cpf}
            customerName={orderDetail.customer?.name}
            show={showEmitFiscalModal}
            updateOrderStatus={handleUpdateOrderStatus}
            closeModal={handleEmitFiscalModal}
          />
        )}
      </Box>

      <Box sx={{ zIndex: 5200 }}>
        <JsonModal
          open={showEventBodyModal}
          title={eventTitle}
          createdBy={eventCreatedBy}
          code={eventCode}
          jsonBodyString={eventBody}
          onClose={handleEventBodyModal}
        />
      </Box>

      <Box sx={{ zIndex: 5200 }}>
        <ExtraDeliveryDialog
          open={extraDeliveryDialogOpen}
          onClose={handleExtraDeliveryDialogClose}
          onConfirm={handleExtraDeliveryConfirm}
          handleSelectChange={handleDriverSelectChange}
          driverError={driverError}
          driverBlockedWarning={driverBlockedWarning}
          reason={deliveryReason}
          setReason={handleReasonChange}
        />
      </Box>
    </>
  );
}
